import React, { useState } from 'react';
import axios from 'axios';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  styled
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QuizIcon from '@mui/icons-material/Quiz';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SchoolIcon from '@mui/icons-material/School';
import { useTheme } from '../ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import GroupIcon from '@mui/icons-material/Group';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import BuildIcon from '@mui/icons-material/Build';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Styled components for modern design
const StyledAppBar = styled(AppBar)(({ theme, mode }) => ({
  background: mode === 'light' 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: '70px',
  padding: theme.spacing(0, 3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0, 2),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const LogoIcon = styled(SchoolIcon)(({ theme }) => ({
  fontSize: '2rem',
  marginRight: theme.spacing(1),
  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Poppins", "Roboto", sans-serif',
  fontWeight: 700,
  letterSpacing: '0.5px',
  background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  margin: theme.spacing(0, 0.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  border: '2px solid rgba(255, 255, 255, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
  },
}));

const StyledMenu = styled(Menu)(({ theme, mode }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    marginTop: theme.spacing(1),
    minWidth: 200,
    boxShadow: mode === 'light' 
      ? '0 8px 32px rgba(0, 0, 0, 0.12)' 
      : '0 8px 32px rgba(0, 0, 0, 0.4)',
    border: mode === 'light' 
      ? '1px solid rgba(0, 0, 0, 0.08)' 
      : '1px solid rgba(255, 255, 255, 0.1)',
    background: mode === 'light' 
      ? 'rgba(255, 255, 255, 0.95)' 
      : 'rgba(30, 30, 30, 0.95)',
    backdropFilter: 'blur(10px)',
    color: mode === 'light' ? '#333' : '#fff',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme, mode }) => ({
  borderRadius: '8px',
  margin: theme.spacing(0.5, 1),
  transition: 'all 0.2s ease',
  color: mode === 'light' ? '#333' : '#fff',
  '&:hover': {
    backgroundColor: mode === 'light' 
      ? 'rgba(102, 126, 234, 0.1)' 
      : 'rgba(144, 202, 249, 0.1)',
    transform: 'translateX(4px)',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 40,
    color: mode === 'light' ? '#666' : '#adb5bd',
  },
  '& .MuiTypography-root': {
    color: mode === 'light' ? '#333' : '#fff',
  },
}));

// New styled components for the sidebar
const StyledDrawer = styled(Drawer)(({ theme, mode, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  position: 'fixed',
  zIndex: 1200,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    background: mode === 'light' 
      ? 'linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)'
      : 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
    borderRight: mode === 'light'
      ? '1px solid rgba(0, 0, 0, 0.08)'
      : '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: mode === 'light'
      ? '4px 0 20px rgba(0, 0, 0, 0.05)'
      : '4px 0 20px rgba(0, 0, 0, 0.3)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    ...(open
      ? {
          width: drawerWidth,
        }
      : {
          width: { xs: 0, sm: theme.spacing(7) },
          [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9),
          },
        }),
  },
}));

const DrawerHeader = styled(Box)(({ theme, mode }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 1.5),
  minHeight: '70px',
  background: mode === 'light'
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
  color: 'white',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const DrawerLogo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  transition: 'opacity 0.3s ease',
}));

const DrawerLogoIcon = styled(SchoolIcon)(({ theme }) => ({
  fontSize: '1.8rem',
  marginRight: theme.spacing(1),
  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
}));

const DrawerLogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"Poppins", "Roboto", sans-serif',
  fontWeight: 600,
  fontSize: '1.1rem',
  letterSpacing: '0.3px',
  background: 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'rotate(90deg)',
  },
}));

const StyledListItem = styled(ListItem)(({ theme, mode, active, open }) => ({
  minHeight: 56,
  margin: theme.spacing(0.5, 1),
  borderRadius: '12px',
  justifyContent: open ? 'flex-start' : 'center',
  padding: theme.spacing(1, 1.5),
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  
  // Active state
  ...(active && {
    background: mode === 'light'
      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
      : 'linear-gradient(135deg, rgba(44, 62, 80, 0.3) 0%, rgba(52, 73, 94, 0.3) 100%)',
    borderLeft: `4px solid ${mode === 'light' ? '#667eea' : '#90caf9'}`,
    boxShadow: mode === 'light'
      ? '0 4px 12px rgba(102, 126, 234, 0.2)'
      : '0 4px 12px rgba(144, 202, 249, 0.2)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: mode === 'light'
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
        : 'linear-gradient(135deg, rgba(144, 202, 249, 0.1) 0%, rgba(100, 181, 246, 0.1) 100%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover::before': {
      opacity: 1,
    },
  }),

  // Hover state
  '&:hover': {
    background: mode === 'light'
      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(144, 202, 249, 0.1) 0%, rgba(100, 181, 246, 0.1) 100%)',
    transform: 'translateX(4px)',
    boxShadow: mode === 'light'
      ? '0 4px 12px rgba(102, 126, 234, 0.15)'
      : '0 4px 12px rgba(144, 202, 249, 0.15)',
  },

  // Icon styling
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: open ? theme.spacing(2) : 'auto',
    justifyContent: 'center',
    color: active 
      ? (mode === 'light' ? '#667eea' : '#90caf9')
      : (mode === 'light' ? '#6c757d' : '#adb5bd'),
    transition: 'all 0.3s ease',
    fontSize: '1.4rem',
  },

  // Text styling
  '& .MuiListItemText-root': {
    opacity: open ? 1 : 0,
    transition: 'opacity 0.3s ease',
    '& .MuiTypography-root': {
      fontWeight: active ? 600 : 500,
      color: active 
        ? (mode === 'light' ? '#667eea' : '#90caf9')
        : (mode === 'light' ? '#495057' : '#e9ecef'),
      fontSize: '0.95rem',
      letterSpacing: '0.3px',
    },
  },
}));

const LogoutListItem = styled(ListItem)(({ theme, mode, open }) => ({
  minHeight: 56,
  margin: theme.spacing(0.5, 1),
  borderRadius: '12px',
  justifyContent: open ? 'flex-start' : 'center',
  padding: theme.spacing(1, 1.5),
  transition: 'all 0.3s ease',
  background: mode === 'light'
    ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(255, 107, 107, 0.1) 100%)'
    : 'linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(255, 107, 107, 0.2) 100%)',
  border: `1px solid ${mode === 'light' ? 'rgba(220, 53, 69, 0.2)' : 'rgba(255, 107, 107, 0.3)'}`,
  
  '&:hover': {
    background: mode === 'light'
      ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(255, 107, 107, 0.2) 100%)'
      : 'linear-gradient(135deg, rgba(220, 53, 69, 0.3) 0%, rgba(255, 107, 107, 0.3) 100%)',
    transform: 'translateX(4px)',
    boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)',
  },

  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: open ? theme.spacing(2) : 'auto',
    justifyContent: 'center',
    color: '#dc3545',
    transition: 'all 0.3s ease',
    fontSize: '1.4rem',
  },

  '& .MuiListItemText-root': {
    opacity: open ? 1 : 0,
    transition: 'opacity 0.3s ease',
    '& .MuiTypography-root': {
      fontWeight: 600,
      color: '#dc3545',
      fontSize: '0.95rem',
      letterSpacing: '0.3px',
    },
  },
}));

const SectionDivider = styled(Divider)(({ theme, mode }) => ({
  margin: theme.spacing(2, 1),
  borderColor: mode === 'light' 
    ? 'rgba(0, 0, 0, 0.08)' 
    : 'rgba(255, 255, 255, 0.08)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '60%',
    height: '1px',
    background: mode === 'light'
      ? 'linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.3) 50%, transparent 100%)'
      : 'linear-gradient(90deg, transparent 0%, rgba(144, 202, 249, 0.3) 50%, transparent 100%)',
  },
}));

const SidebarSection = styled(Box)(({ theme, mode }) => ({
  padding: theme.spacing(1, 0),
  '& .MuiTypography-root': {
    padding: theme.spacing(0.5, 2),
    fontSize: '0.75rem',
    fontWeight: 600,
    color: mode === 'light' ? '#6c757d' : '#adb5bd',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const drawerWidth = 240;

const userRoles = {
  superAdmin: '4',
  coachingAdmin: '1'
};

const DashboardLayout = ({ user }) => {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [crudExpanded, setCrudExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();

  const userRole = localStorage.getItem('userRole');

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    
    if (axios && axios.defaults && axios.defaults.headers) {
      delete axios.defaults.headers.common['Authorization'];
    }
    
    window.location.href = '/login';
  };

  const getRoleLabel = () => {
    switch (Number(userRole)) {
      case 1: return 'Coaching Admin';
      case 2: return 'Teacher';
      case 4: return 'Super Admin';
      default: return 'Student';
    }
  };

  const getRoleColor = () => {
    switch (Number(userRole)) {
      case 1: return '#4caf50';
      case 2: return '#2196f3';
      case 4: return '#f44336';
      default: return '#9c27b0';
    }
  };

  const baseMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Tests', icon: <QuizIcon />, path: '/tests' },
    { text: 'Performance', icon: <AssessmentIcon />, path: '/performance' },
    { text: 'Materials', icon: <LibraryBooksIcon />, path: '/materials' },
    { text: 'AI Assistant', icon: <SmartToyIcon />, path: '/ai-assistant' },
    { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' }
  ];

  // CRUD Menu Items (Only for Super Admin and Coaching Admin)
  const crudMenuItems = (userRole === userRoles.superAdmin || userRole === userRoles.coachingAdmin) ? [
    { text: 'Materials', icon: <LibraryBooksIcon />, path: '/crud/materials' }
  ] : [];

  let roleSpecificMenuItems = [];
  if (userRole === userRoles.superAdmin) {
    roleSpecificMenuItems = [
      { text: 'Super Admin Users', icon: <ManageAccountsIcon />, path: '/admin/users' },
      { text: 'Batch Management', icon: <GroupIcon />, path: '/batch-management' },
    ];
  } else if (userRole === userRoles.coachingAdmin) {
    roleSpecificMenuItems = [
      { text: 'Coaching Admin Users', icon: <ManageAccountsIcon />, path: '/coaching/users' },
      { text: 'Batch Management', icon: <GroupIcon />, path: '/batch-management' },
    ];
  }

  const filteredMenuItems = [...baseMenuItems, ...roleSpecificMenuItems];

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <StyledAppBar
        position="fixed"
        mode={mode}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { 
            xs: '100%', 
            sm: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${muiTheme.spacing(9)}px)` 
          },
                                marginLeft: { 
            xs: 0, 
            sm: open ? drawerWidth : muiTheme.spacing(9)
          },
          transition: muiTheme.transitions.create(['width', 'margin'], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <StyledToolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={toggleDrawer}
            edge="start"
            sx={{
              marginRight: 2,
              display: { xs: 'flex', sm: 'flex' }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo and Brand */}
          <LogoContainer component={Link} to="/dashboard">
            <LogoIcon />
            <LogoText variant="h6" noWrap>
              UPSC Platform
            </LogoText>
          </LogoContainer>

          <Box sx={{ flexGrow: 1 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Notifications */}
            <Tooltip title="Notifications">
              <ActionButton>
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon />
                </Badge>
              </ActionButton>
            </Tooltip>

          {/* Theme Toggle */}
          <Tooltip title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}>
              <ActionButton onClick={toggleTheme}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </ActionButton>
          </Tooltip>

            {/* User Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <Chip
                label={getRoleLabel()}
                size="small"
                sx={{
                  backgroundColor: getRoleColor(),
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
                  sx={{ p: 0 }}
              aria-controls={Boolean(anchorEl) ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
            >
                  <UserAvatar>
                {user?.name?.charAt(0) || 'U'}
                  </UserAvatar>
            </IconButton>
          </Tooltip>
            </Box>
          </Box>
        </StyledToolbar>
      </StyledAppBar>

      {/* Profile/Settings Menu */}
      <StyledMenu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        mode={mode}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography 
            variant="subtitle1" 
            fontWeight={600}
            sx={{ color: mode === 'light' ? '#333' : '#fff' }}
          >
            {user?.name || 'User'}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ color: mode === 'light' ? '#666' : '#adb5bd' }}
          >
            {user?.email || 'user@example.com'}
          </Typography>
        </Box>
        <Divider sx={{ 
          borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)' 
        }} />
        <StyledMenuItem component={Link} to="/profile" mode={mode}>
          <PersonIcon />
          <Typography sx={{ ml: 1 }}>Profile</Typography>
        </StyledMenuItem>
        <StyledMenuItem component={Link} to="/settings" mode={mode}>
          <SettingsIcon />
          <Typography sx={{ ml: 1 }}>Settings</Typography>
        </StyledMenuItem>
        <Divider sx={{ 
          borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)' 
        }} />
        <StyledMenuItem onClick={handleLogout} mode={mode}>
          <LogoutIcon />
          <Typography sx={{ ml: 1 }}>Logout</Typography>
        </StyledMenuItem>
      </StyledMenu>

      {/* Drawer and Main Content */}
      <StyledDrawer
        variant="permanent"
        open={open}
        mode={mode}
        sx={{
          display: { xs: open ? 'block' : 'none', sm: 'block' }
        }}
      >
        <DrawerHeader mode={mode}>
          <DrawerLogo>
            <DrawerLogoIcon />
            <DrawerLogoText>UPSC Platform</DrawerLogoText>
          </DrawerLogo>
          <CloseButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </CloseButton>
        </DrawerHeader>
        <Divider />
        <List>
          {filteredMenuItems.map((item) => (
             <StyledListItem
              key={item.text}
              component={Link}
              to={item.path}
               active={location.pathname === item.path}
               open={open}
               mode={mode}
             >
               <ListItemIcon>
                {item.icon}
              </ListItemIcon>
               <ListItemText primary={item.text} />
             </StyledListItem>
          ))}
        </List>

        {/* CRUD Section (Only for Super Admin and Coaching Admin) */}
        {crudMenuItems.length > 0 && (
          <>
            <SectionDivider mode={mode} />
            <List>
              <StyledListItem
                onClick={() => setCrudExpanded(!crudExpanded)}
                open={open}
                mode={mode}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemIcon>
                  <BuildIcon />
                </ListItemIcon>
                <ListItemText primary="CRUD Management" />
                {open && (crudExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </StyledListItem>
              
              {crudExpanded && crudMenuItems.map((item) => (
                <StyledListItem
                  key={item.text}
                  component={Link}
                  to={item.path}
                  active={location.pathname === item.path}
                  open={open}
                  mode={mode}
                  sx={{ pl: 4 }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </StyledListItem>
              ))}
            </List>
          </>
        )}
                 <SectionDivider mode={mode} />
        <List>
           <LogoutListItem
            onClick={handleLogout}
             open={open}
             mode={mode}
           >
             <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
             <ListItemText primary="Logout" />
           </LogoutListItem>
        </List>
      </StyledDrawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0.5, sm: 1, md: 1.5 },
          width: '100%',
          marginLeft: { 
            xs: 0, 
            sm: open ? muiTheme.spacing(32) : muiTheme.spacing(9)
          },
          transition: muiTheme.transitions.create(['width', 'margin'], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.leavingScreen,
          }),
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          pt: { xs: 0, sm: 0 }
        }}
      >
        <Toolbar sx={{ mb: { xs: 0.5, sm: 1, md: 1.5 } }} />
        <Box sx={{
          width: '100%',
          boxSizing: 'border-box',        
          mx: 'auto',
          px: { xs: 1, sm: 2, md: 3 },
          pb: { xs: 6, sm: 3 }
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;